import { TextNode, EditorConfig, LexicalNode, NodeKey, SerializedTextNode, Spread } from "lexical";

export type SceneBreakPayload = {
    sceneId: string;
    sceneIndex: number;
    /** Script-clip local (source) time domain. */
    startSec: number;
    /** Script-clip local (source) time domain. */
    endSec: number;
};

export type SerializedSceneBreakNode = Spread<
    {
        type: "scene-break";
        version: 1;
        payload: SceneBreakPayload;
    },
    SerializedTextNode
>;

export class SceneBreakNode extends TextNode {
    __payload: SceneBreakPayload;

    static getType(): string {
        return "scene-break";
    }

    static clone(node: SceneBreakNode): SceneBreakNode {
        return new SceneBreakNode("/", { ...node.__payload }, node.__key);
    }

    constructor(text: string, payload: SceneBreakPayload, key?: NodeKey) {
        super(text, key);
        this.__payload = payload;
        // Treat as an atomic token so we don't land a caret "inside" it.
        this.setMode("token");
    }

    createDOM(_config: EditorConfig): HTMLElement {
        const container = document.createElement("span");
        container.textContent = "/";
        container.style.display = "inline-flex";
        container.style.alignItems = "center";
        container.style.justifyContent = "center";
        container.style.padding = "1px 6px";
        container.style.borderRadius = "6px";
        container.style.marginRight = "8px";
        container.style.fontFamily = "Inter, sans-serif";
        container.style.fontSize = "13px";
        container.style.fontWeight = "700";
        container.style.lineHeight = "18px";
        container.style.color = "#6B7280";
        container.style.backgroundColor = "rgba(156, 163, 175, 0.18)";
        container.style.cursor = "pointer";
        container.style.userSelect = "text";
        container.style.transition = "background-color 120ms ease";
        container.classList.add("transcript-scene-break");
        (container as any).dataset.sceneIndex = String(this.__payload.sceneIndex);

        container.addEventListener("mouseenter", () => {
            container.style.backgroundColor = "rgba(156, 163, 175, 0.28)";
        });
        container.addEventListener("mouseleave", () => {
            container.style.backgroundColor = "rgba(156, 163, 175, 0.18)";
        });

        return container;
    }

    updateDOM(prevNode: SceneBreakNode, dom: HTMLElement): boolean {
        if (prevNode.__payload.sceneIndex !== this.__payload.sceneIndex) {
            (dom as any).dataset.sceneIndex = String(this.__payload.sceneIndex);
        }
        return false;
    }

    static importJSON(serializedNode: SerializedSceneBreakNode): SceneBreakNode {
        const node = new SceneBreakNode(serializedNode.text ?? "/", serializedNode.payload);
        node.setFormat(serializedNode.format);
        node.setDetail(serializedNode.detail);
        node.setMode(serializedNode.mode);
        node.setStyle(serializedNode.style);
        return node;
    }

    exportJSON(): SerializedSceneBreakNode {
        return {
            ...super.exportJSON(),
            type: "scene-break",
            version: 1,
            payload: this.__payload,
        };
    }

    getPayload(): SceneBreakPayload {
        return this.__payload;
    }
}

export function $createSceneBreakNode(payload: SceneBreakPayload): SceneBreakNode {
    return new SceneBreakNode("/", payload);
}

export function $isSceneBreakNode(node?: LexicalNode | null): node is SceneBreakNode {
    return node instanceof SceneBreakNode;
}

